import { type AxiosRequestConfig, type AxiosResponse } from 'axios';

import { type Result, ResultFactory } from '@treneo/models';
import { REQUEST_CANCELED } from '@treneo/shared/constants';

import type { ApiClient } from './api-client';

interface AxiosErrorResponse {
  response?: {
    data?: unknown;
  };
  message?: string;
}

const MESSAGE_KEYS = [
  'detail',
  'message',
  'Message',
  'reason',
  'Reason',
  'error',
  'Error',
  'errorTranslationCode'
] as const;

const getGeneralErrorsArray = (errorData: Record<string, unknown>): string[] => {
  const errs = errorData.errors ?? errorData.Errors;
  if (errs == null || typeof errs !== 'object') {
    return [];
  }
  const generalErrors =
    (errs as Record<string, unknown>)['GeneralErrors'] ??
    (errs as Record<string, unknown>)['generalErrors'];
  if (!Array.isArray(generalErrors)) {
    return [];
  }
  return generalErrors.filter(
    (item): item is string => typeof item === 'string' && item.trim().length > 0
  );
};

const getSingleMessageFromErrorObject = (errorData: Record<string, unknown>): string | null => {
  const generalErrors = getGeneralErrorsArray(errorData);
  if (generalErrors.length > 0) {
    return generalErrors[0];
  }
  for (const key of MESSAGE_KEYS) {
    const value = errorData[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }
  for (const value of Object.values(errorData)) {
    if (typeof value === 'string' && value.length > 0 && value.length < 1000) {
      return value;
    }
  }
  return null;
};

export const extractErrorMessages = (error: unknown): string[] => {
  const err = error as AxiosErrorResponse;
  const rawData = err?.response?.data;

  if (rawData == null) {
    const msg = err?.message;
    return typeof msg === 'string' && msg.trim().length > 0 ? [msg] : [];
  }

  if (typeof rawData === 'string') {
    const str = rawData.length > 500 ? `${rawData.slice(0, 500)}...` : rawData;
    return str.trim().length > 0 ? [str] : [];
  }

  const asObject = rawData as Record<string, unknown>;
  const generalErrors = getGeneralErrorsArray(asObject);
  if (generalErrors.length > 0) {
    return generalErrors;
  }
  const single = getSingleMessageFromErrorObject(asObject);
  if (single != null) {
    return [single];
  }
  const msg = err?.message;
  return typeof msg === 'string' && msg.trim().length > 0 ? [msg] : [];
};

const extractErrorMessage = (error: unknown, fallbackMessage: string): string => {
  const messages = extractErrorMessages(error);
  if (messages.length === 0) {
    return fallbackMessage;
  }
  return messages.length > 1 ? messages.join('\n') : messages[0];
};

const failureResult = <T>(error: string): Result<T> => ({
  data: null,
  error,
  isSuccess: false,
  isFailure: true
});

export abstract class BaseApi {
  protected abstract readonly baseUrl: string;
  protected readonly apiClient: ApiClient;

  constructor(apiClient: ApiClient) {
    this.apiClient = apiClient;
  }

  protected async executeResponse<T>(
    apiCall: () => Promise<AxiosResponse<T>>,
    errorMessage: string
  ): Promise<Result<T>> {
    try {
      const response = await apiCall();
      return ResultFactory.success(response.data);
    } catch (error: unknown) {
      if (this.isCancellationError(error)) {
        return failureResult<T>(REQUEST_CANCELED);
      }
      const message = extractErrorMessage(error, errorMessage);
      return failureResult<T>(message);
    }
  }

  private isCancellationError(error: unknown): boolean {
    if (error == null || typeof error !== 'object') {
      return false;
    }
    const err = error as { name?: string; code?: string };
    return err.name === 'AbortError' || err.code === 'ERR_CANCELED';
  }

  protected async executeVoid(apiCall: () => Promise<AxiosResponse<unknown>>): Promise<void> {
    await apiCall();
  }

  protected async get<T>(
    url: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- API callers pass null or query objects; strict type breaks call sites
    params?: any,
    errorMessage = 'Request failed',
    signal?: AbortSignal,
    timeout?: number
  ): Promise<Result<T>> {
    const fullUrl = `${this.baseUrl}${url}`;
    const config: AxiosRequestConfig = {};

    if (params) {
      config.params = params;
    }

    if (signal) {
      config.signal = signal;
    }

    if (timeout != null) {
      config.timeout = timeout;
    }

    return this.executeResponse(() => this.apiClient.get<T>(fullUrl, config), errorMessage);
  }

  protected async post<T>(
    url: string,
    data?: unknown,
    errorMessage = 'Request failed',
    signal?: AbortSignal,
    timeout?: number
  ): Promise<Result<T>> {
    const fullUrl = `${this.baseUrl}${url}`;
    const config: AxiosRequestConfig = {};

    if (signal) {
      config.signal = signal;
    }

    if (timeout != null) {
      config.timeout = timeout;
    }

    return this.executeResponse(() => this.apiClient.post<T>(fullUrl, data, config), errorMessage);
  }

  protected async put<T>(
    url: string,
    data?: unknown,
    errorMessage = 'Request failed'
  ): Promise<Result<T>> {
    const fullUrl = `${this.baseUrl}${url}`;
    return this.executeResponse(() => this.apiClient.put<T>(fullUrl, data), errorMessage);
  }

  protected async patch<T>(
    url: string,
    data?: unknown,
    errorMessage = 'Request failed'
  ): Promise<Result<T>> {
    const fullUrl = `${this.baseUrl}${url}`;
    return this.executeResponse(() => this.apiClient.patch<T>(fullUrl, data), errorMessage);
  }

  protected async delete<T>(
    url: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- API callers pass null or query objects
    params?: any,
    errorMessage = 'Request failed'
  ): Promise<Result<T>> {
    const fullUrl = `${this.baseUrl}${url}`;
    const config: AxiosRequestConfig | undefined = params ? { params } : undefined;
    return this.executeResponse(() => this.apiClient.delete<T>(fullUrl, config), errorMessage);
  }

  protected async postVoid(url: string, data?: unknown): Promise<void> {
    const fullUrl = `${this.baseUrl}${url}`;
    return this.executeVoid(() => this.apiClient.post(fullUrl, data));
  }

  protected async putVoid(url: string, data?: unknown): Promise<void> {
    const fullUrl = `${this.baseUrl}${url}`;
    return this.executeVoid(() => this.apiClient.put(fullUrl, data));
  }

  protected async patchVoid(url: string, data?: unknown): Promise<void> {
    const fullUrl = `${this.baseUrl}${url}`;
    return this.executeVoid(() => this.apiClient.patch(fullUrl, data));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- API callers pass null or query objects
  protected async deleteVoid(url: string, params?: any): Promise<void> {
    const fullUrl = `${this.baseUrl}${url}`;
    return this.executeVoid(() => this.apiClient.delete(fullUrl, params ? { params } : undefined));
  }

  protected buildUrl(path: string, ...params: (string | number)[]): string {
    let url = `${this.baseUrl}${path}`;
    params.forEach(param => {
      url = url.replace(/\{[^}]+\}/, String(param));
    });
    return url;
  }

  protected buildRouteUrl(route: string, params: Record<string, string | number>): string {
    let url = `${this.baseUrl}${route}`;
    Object.entries(params).forEach(([key, value]) => {
      url = url.replace(`{${key}}`, String(value));
    });
    return url;
  }
}
